import type {
  ColorByMode,
  CompareOp,
  MatchMode,
  TaktAttribute,
} from "./attributes";

/**
 * PostHog event payloads for every "view" action on the Takt board — the ways a
 * user reshapes what they're looking at without mutating the schedule: applying
 * or clearing filters, switching the color dimension, and flipping the
 * all/any match mode. The board's filter/color handlers (in `useTaktBoard`) are
 * the single chokepoint these route through, so firing here captures every
 * surface (the Filters rail, the applied-filter chip bar, the empty-state
 * button) without scattering telemetry across components.
 *
 * Builders are pure — they return `{ event, properties }` so the payload shape
 * stays unit-testable; the hook does the fire-and-forget `trackEvent`.
 */

export const TAKT_VIEW_EVENTS = {
  /** A single filter value was toggled on/off, or a numeric compare was set. */
  filterChanged: "takt_filter_changed",
  /** One attribute's filter, or all filters, were cleared. */
  filterCleared: "takt_filter_cleared",
  /** The color-by dimension changed. */
  colorChanged: "takt_color_changed",
  /** The all/any match mode flipped. */
  matchModeChanged: "takt_match_mode_changed",
} as const;

export interface TrackedEvent {
  event: string;
  properties: Record<string, unknown>;
}

/** An enum/boolean filter value was toggled. `selected` = added (true) or
 *  removed (false). Carries which filter and value, with human labels resolved
 *  off the attribute so PostHog reads "Trade: Electrical" not "trade: 42". */
export function filterValueChangedEvent(
  attr: TaktAttribute | undefined,
  attrKey: string,
  valueId: string,
  selected: boolean,
  matchMode: MatchMode,
): TrackedEvent {
  const value = attr?.domain.find((v) => v.id === valueId);
  const properties: Record<string, unknown> = {
    attribute: attrKey,
    attribute_label: attr?.label ?? attrKey,
    kind: attr?.kind ?? "enum",
    value: valueId,
    value_label: value?.label ?? valueId,
    selected,
    match_mode: matchMode,
  };
  if (value?.code) properties.value_code = value.code;
  return { event: TAKT_VIEW_EVENTS.filterChanged, properties };
}

/** A numeric compare (range) filter was set or cleared. `value === null` clears
 *  it (selected=false). */
export function compareFilterChangedEvent(
  attr: TaktAttribute | undefined,
  attrKey: string,
  op: CompareOp,
  value: number | null,
  matchMode: MatchMode,
): TrackedEvent {
  const selected = value != null && !Number.isNaN(value);
  const properties: Record<string, unknown> = {
    attribute: attrKey,
    attribute_label: attr?.label ?? attrKey,
    kind: "range",
    op,
    threshold: selected ? value : null,
    selected,
    match_mode: matchMode,
  };
  if (attr?.unit) properties.unit = attr.unit;
  return { event: TAKT_VIEW_EVENTS.filterChanged, properties };
}

/** One attribute's filter was cleared (scope "attribute") or every filter was
 *  cleared at once (scope "all"). */
export function filterClearedEvent(
  scope: "attribute" | "all",
  attr?: TaktAttribute,
  attrKey?: string,
): TrackedEvent {
  const properties: Record<string, unknown> = { scope };
  if (scope === "attribute" && attrKey) {
    properties.attribute = attrKey;
    properties.attribute_label = attr?.label ?? attrKey;
  }
  return { event: TAKT_VIEW_EVENTS.filterCleared, properties };
}

export function colorChangedEvent(mode: ColorByMode): TrackedEvent {
  return { event: TAKT_VIEW_EVENTS.colorChanged, properties: { mode } };
}

export function matchModeChangedEvent(mode: MatchMode): TrackedEvent {
  return { event: TAKT_VIEW_EVENTS.matchModeChanged, properties: { mode } };
}
